import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, ChevronDown, MapPin, Briefcase, Clock } from "lucide-react";
import api from "../../lib/api";
import { useSeo } from "./useSeo";
import { useEnquiry } from "./enquiryContext";
import PageHero from "./PageHero";
import { IMAGES } from "./siteData";

const CULTURE = [
  { t: "Partner mentorship", d: "Articles and associates work directly with partners on live files — not on the sidelines." },
  { t: "Breadth early", d: "Rotate across audit, tax, transactions and advisory before you specialise." },
  { t: "Modern tooling", d: "Our in-house portal, calculators and knowledge library cut the grunt work so you learn the judgement." },
];

function JobRow({ j, open, onToggle, onApply }) {
  return (
    <div className="border-b border-slate-200" data-testid={`job-row-${j.id}`}>
      <button onClick={onToggle} className="w-full py-7 grid md:grid-cols-12 gap-3 md:gap-6 items-center text-left group" data-testid={`job-toggle-${j.id}`}>
        <span className="md:col-span-6 font-serif text-2xl font-semibold text-[#14263D] group-hover:text-[#B09558] transition-colors">{j.title}</span>
        <span className="md:col-span-2 text-[11px] uppercase tracking-[0.2em] text-[#C4A66A]">{j.category}</span>
        <span className="md:col-span-3 text-sm text-slate-500 flex items-center gap-2"><MapPin className="h-3.5 w-3.5" />{j.location}</span>
        <ChevronDown className={`md:col-span-1 justify-self-end h-5 w-5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-8 grid md:grid-cols-12 gap-6 site-reveal" data-testid={`job-detail-${j.id}`}>
          <div className="md:col-span-8">
            <div className="flex flex-wrap gap-5 text-sm text-slate-500">
              {j.experience && <span className="flex items-center gap-2"><Briefcase className="h-4 w-4" />{j.experience}</span>}
              <span className="flex items-center gap-2"><Clock className="h-4 w-4" />{j.type}</span>
            </div>
            <p className="mt-5 text-slate-700 leading-relaxed">{j.description}</p>
            {j.requirements?.length > 0 && (
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                {j.requirements.map((r) => <li key={r} className="flex gap-3"><span className="h-1.5 w-1.5 bg-[#C4A66A] mt-2 shrink-0" />{r}</li>)}
              </ul>
            )}
          </div>
          <div className="md:col-span-4 md:text-right">
            <button onClick={onApply} className="btn-site-navy h-12 px-7 text-sm font-semibold inline-flex items-center gap-2" data-testid={`job-apply-${j.id}`}>Apply for this role <ArrowUpRight className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Careers() {
  useSeo("Careers & Articleship Opportunities", "CA articleship and qualified Chartered Accountant roles at VCAJ & Associates LLP. Apply online with your CV.");
  const openEnquiry = useEnquiry();
  const [jobs, setJobs] = useState(null);
  const [cat, setCat] = useState("All");
  const [openId, setOpenId] = useState(null);
  useEffect(() => { api.get("/public/site/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([])); }, []);
  const cats = useMemo(() => ["All", ...new Set((jobs || []).map((j) => j.category))], [jobs]);
  const shown = (jobs || []).filter((j) => cat === "All" || j.category === cat);
  const apply = (position = "") => openEnquiry({ kind: "application", service: "Careers / Articleship", position });

  return (
    <div data-testid="careers-page">
      <PageHero testid="careers-hero" image={IMAGES.careers} eyebrow="Careers" title="Build judgement, not just hours."
        intro="Articleship and qualified roles for people who want to learn the craft properly — with partners who teach." />
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-10">
          {CULTURE.map((c, i) => (
            <div key={c.t} className="border-t-2 border-[#C4A66A] pt-6" data-testid={`culture-item-${i}`}>
              <h3 className="font-serif text-2xl font-semibold text-[#14263D]">{c.t}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed text-sm">{c.d}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="py-24 bg-[#F8FAFC] border-t border-slate-200" data-testid="careers-openings">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="site-eyebrow">Open positions</div>
              <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-[#14263D] mt-4">Current openings.</h2>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="careers-filters">
              {cats.map((c) => (
                <button key={c} onClick={() => setCat(c)} className={`px-4 h-9 text-xs font-semibold tracking-wide border transition-colors ${cat === c ? "bg-[#14263D] text-white border-[#14263D]" : "border-slate-300 text-slate-600 hover:border-[#14263D]"}`} data-testid={`careers-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c}</button>
              ))}
            </div>
          </div>
          <div className="mt-10 border-t border-slate-200">
            {jobs === null && <div className="py-8 text-sm text-slate-500">Loading…</div>}
            {jobs !== null && shown.length === 0 && <div className="py-8 text-slate-500" data-testid="careers-empty">No openings in this category right now — send us your profile below.</div>}
            {shown.map((j) => <JobRow key={j.id} j={j} open={openId === j.id} onToggle={() => setOpenId(openId === j.id ? null : j.id)} onApply={() => apply(j.title)} />)}
          </div>
        </div>
      </section>
      <section className="py-20 bg-[#14263D] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h2 className="font-serif text-3xl lg:text-4xl font-semibold">Don't see your role?</h2>
            <p className="text-white/60 mt-3">Send us your CV — we keep strong profiles on file.</p>
          </div>
          <button onClick={() => apply("")} className="btn-site-gold h-12 px-8 text-sm font-semibold inline-flex items-center gap-2 w-fit" data-testid="careers-general-apply-btn">Send your profile <ArrowUpRight className="h-4 w-4" /></button>
        </div>
      </section>
    </div>
  );
}
